import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { RequireAuth, RequireGuest, RequirePermission, RequirePlatformAdmin, RequirePortalRole, RequireStaffRole } from '@/lib/auth/guards';
import { EmptyState } from '@/components/ui/states';
import { useAppSelector, selectPermissions } from '@/app/store';
import { toPermissionSet, canAny } from '@/lib/auth/permissions';

/**
 * Route table.
 *
 * Every page is lazy so the initial bundle stays small (SRS §10: < 2s page
 * load). Routes are permission-gated to match the sidebar, so a deep link
 * can't bypass what the nav hides.
 */

const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/ResetPasswordPage'));
const OtpLoginPage = lazy(() => import('@/features/auth/OtpLoginPage'));
const OAuthCallbackPage = lazy(() => import('@/features/auth/OAuthCallbackPage'));
const PrivacyPolicyPage = lazy(() => import('@/features/legal/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('@/features/legal/TermsOfServicePage'));
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const ExecutiveDashboardPage = lazy(() => import('@/features/dashboard/ExecutiveDashboardPage'));
const ProjectsListPage = lazy(() => import('@/features/projects/ProjectsListPage'));
const ProjectPortfolioPage = lazy(() => import('@/features/projects/ProjectPortfolioPage'));
const CreateProjectPage = lazy(() => import('@/features/projects/CreateProjectPage'));
const EditProjectPage = lazy(() => import('@/features/projects/EditProjectPage'));
const ProjectSettingsPage = lazy(() => import('@/features/projects/ProjectSettingsPage'));
const ProjectCategoriesTemplatesPage = lazy(() => import('@/features/projects/ProjectCategoriesTemplatesPage'));
const ProjectLayout = lazy(() => import('@/features/projects/ProjectLayout'));
const OverviewTab = lazy(() => import('@/features/projects/tabs/OverviewTab'));
const BoardTab = lazy(() => import('@/features/projects/tabs/BoardTab'));
const SprintsTab = lazy(() => import('@/features/projects/tabs/SprintsTab'));
const MilestonesTab = lazy(() => import('@/features/projects/tabs/MilestonesTab'));
const TimelineTab = lazy(() => import('@/features/projects/tabs/TimelineTab'));
const DiscussionTab = lazy(() => import('@/features/projects/tabs/DiscussionTab'));
const ChatTab = lazy(() => import('@/features/projects/tabs/ChatTab'));
const TasksListPage = lazy(() => import('@/features/projects/TasksListPage'));
const CreateTaskPage = lazy(() => import('@/features/projects/CreateTaskPage'));
const EditTaskPage = lazy(() => import('@/features/projects/EditTaskPage'));
const TaskDetailPage = lazy(() => import('@/features/projects/TaskDetailPage'));
const CalendarPage = lazy(() => import('@/features/calendar/CalendarPage'));
const NotificationsPage = lazy(() => import('@/features/notifications/NotificationsPage'));
const PlatformShell = lazy(() => import('@/features/platform/PlatformShell'));
const PlatformOverviewPage = lazy(() => import('@/features/platform/PlatformOverviewPage'));
const PlatformTenantsPage = lazy(() => import('@/features/platform/PlatformTenantsPage'));
const PlatformPlansPage = lazy(() => import('@/features/platform/PlatformPlansPage'));
const PlatformRevenuePage = lazy(() => import('@/features/platform/PlatformRevenuePage'));
const PlatformUsagePage = lazy(() => import('@/features/platform/PlatformUsagePage'));
const PlatformAuditLogPage = lazy(() => import('@/features/platform/PlatformAuditLogPage'));
const PlatformTicketsPage = lazy(() => import('@/features/platform/PlatformTicketsPage'));
const PlatformSettingsPage = lazy(() => import('@/features/platform/PlatformSettingsPage'));
const PlatformAdminsPage = lazy(() => import('@/features/platform/PlatformAdminsPage'));
const PlatformMarketplaceReviewPage = lazy(() => import('@/features/platform/PlatformMarketplaceReviewPage'));
const KioskDisplayPage = lazy(() => import('@/features/hrms/KioskDisplayPage'));
const ScanConfirmPage = lazy(() => import('@/features/hrms/ScanConfirmPage'));
const CrmLayout = lazy(() => import('@/features/crm/CrmLayout'));
const LeadsListPage = lazy(() => import('@/features/crm/LeadsListPage'));
const LeadDetailPage = lazy(() => import('@/features/crm/LeadDetailPage'));
const LeadFormPage = lazy(() => import('@/features/crm/LeadFormPage'));
const DealsBoardPage = lazy(() => import('@/features/crm/DealsBoardPage'));
const DealDetailPage = lazy(() => import('@/features/crm/DealDetailPage'));
const DealFormPage = lazy(() => import('@/features/crm/DealFormPage'));
const CompaniesListPage = lazy(() => import('@/features/crm/CompaniesListPage'));
const CompanyDetailPage = lazy(() => import('@/features/crm/CompanyDetailPage'));
const CompanyFormPage = lazy(() => import('@/features/crm/CompanyFormPage'));
const ContactsListPage = lazy(() => import('@/features/crm/ContactsListPage'));
const ContactDetailPage = lazy(() => import('@/features/crm/ContactDetailPage'));
const ContactFormPage = lazy(() => import('@/features/crm/ContactFormPage'));
const SequencesListPage = lazy(() => import('@/features/sequences/SequencesListPage'));
const SequenceDetailPage = lazy(() => import('@/features/sequences/SequenceDetailPage'));
const UnsubscribePage = lazy(() => import('@/features/sequences/UnsubscribePage'));
const QuoteSigningPage = lazy(() => import('@/features/quote-signing/QuoteSigningPage'));
const QuotationsListPage = lazy(() => import('@/features/crm/QuotationsListPage'));
const QuoteTemplatesPage = lazy(() => import('@/features/crm/QuoteTemplatesPage'));
const AiEmployeesPage = lazy(() => import('@/features/ai-employees/AiEmployeesPage'));
const AiAssistantPage = lazy(() => import('@/features/ai-assistant/AiAssistantPage'));
const InboxPage = lazy(() => import('@/features/inbox/InboxPage'));
const WhatsAppInboxPage = lazy(() => import('@/features/whatsapp/WhatsAppInboxPage'));
const PortalTicketsInboxPage = lazy(() => import('@/features/portal-ticket/PortalTicketsInboxPage'));
const WhatsAppCampaignsListPage = lazy(() => import('@/features/whatsapp/WhatsAppCampaignsListPage'));
const WhatsAppCampaignDetailPage = lazy(() => import('@/features/whatsapp/WhatsAppCampaignDetailPage'));
const InvoicesListPage = lazy(() => import('@/features/finance/InvoicesListPage'));
const FinanceDashboardPage = lazy(() => import('@/features/finance/FinanceDashboardPage'));
const InvoiceDetailPage = lazy(() => import('@/features/finance/InvoiceDetailPage'));
const InvoiceFormPage = lazy(() => import('@/features/finance/InvoiceFormPage'));
const ExpensesListPage = lazy(() => import('@/features/finance/ExpensesListPage'));
const ExpenseCategoriesPage = lazy(() => import('@/features/finance/ExpenseCategoriesPage'));
const InvoiceTemplatesPage = lazy(() => import('@/features/finance/InvoiceTemplatesPage'));
const FinanceSettingsPage = lazy(() => import('@/features/finance/FinanceSettingsPage'));
const RecurringInvoicesPage = lazy(() => import('@/features/finance/RecurringInvoicesPage'));
const BankAccountsPage = lazy(() => import('@/features/finance/BankAccountsPage'));
const PaymentsPage = lazy(() => import('@/features/finance/PaymentsPage'));
const CustomerLedgerPage = lazy(() => import('@/features/finance/CustomerLedgerPage'));
const AdvancesPage = lazy(() => import('@/features/finance/AdvancesPage'));
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'));
const ReportBuilderPage = lazy(() => import('@/features/reports/ReportBuilderPage'));
const ProjectTaskReportsPage = lazy(() => import('@/features/reports/ProjectTaskReportsPage'));
const SalesReportsPage = lazy(() => import('@/features/reports/SalesReportsPage'));
const SalesTargetsPage = lazy(() => import('@/features/reports/SalesTargetsPage'));
const RetainersPage = lazy(() => import('@/features/agency/RetainersPage'));
const MarketingCampaignsPage = lazy(() => import('@/features/agency/MarketingCampaignsPage'));
const MyAppsPage = lazy(() => import('@/features/developer-portal/MyAppsPage'));
const MarketplaceBrowsePage = lazy(() => import('@/features/marketplace/MarketplaceBrowsePage'));
const MyInstallationsPage = lazy(() => import('@/features/marketplace/MyInstallationsPage'));
const AssetsListPage = lazy(() => import('@/features/assets/AssetsListPage'));
const MyAssetsPage = lazy(() => import('@/features/assets/MyAssetsPage'));
const AssetDetailPage = lazy(() => import('@/features/assets/AssetDetailPage'));
const AssetFormPage = lazy(() => import('@/features/assets/AssetFormPage'));
const AssetCategoriesPage = lazy(() => import('@/features/assets/AssetCategoriesPage'));
const InventoryListPage = lazy(() => import('@/features/inventory/InventoryListPage'));
const ProductDetailPage = lazy(() => import('@/features/inventory/ProductDetailPage'));
const ProductFormPage = lazy(() => import('@/features/inventory/ProductFormPage'));
const ProductCategoriesPage = lazy(() => import('@/features/inventory/ProductCategoriesPage'));
const WarehousesPage = lazy(() => import('@/features/inventory/WarehousesPage'));
const ProcurementLayout = lazy(() => import('@/features/procurement/ProcurementLayout'));
const VendorsListPage = lazy(() => import('@/features/procurement/VendorsListPage'));
const VendorDetailPage = lazy(() => import('@/features/procurement/VendorDetailPage'));
const VendorFormPage = lazy(() => import('@/features/procurement/VendorFormPage'));
const VendorCategoriesPage = lazy(() => import('@/features/procurement/VendorCategoriesPage'));
const PurchaseRequestsListPage = lazy(() => import('@/features/procurement/PurchaseRequestsListPage'));
const PurchaseRequestDetailPage = lazy(() => import('@/features/procurement/PurchaseRequestDetailPage'));
const PurchaseRequestFormPage = lazy(() => import('@/features/procurement/PurchaseRequestFormPage'));
const RfqsListPage = lazy(() => import('@/features/procurement/RfqsListPage'));
const RfqDetailPage = lazy(() => import('@/features/procurement/RfqDetailPage'));
const RfqFormPage = lazy(() => import('@/features/procurement/RfqFormPage'));
const PurchaseOrdersListPage = lazy(() => import('@/features/procurement/PurchaseOrdersListPage'));
const PurchaseOrderDetailPage = lazy(() => import('@/features/procurement/PurchaseOrderDetailPage'));
const PurchaseOrderFormPage = lazy(() => import('@/features/procurement/PurchaseOrderFormPage'));
const FinanceLayout = lazy(() => import('@/features/finance/FinanceLayout'));
const ChartOfAccountsPage = lazy(() => import('@/features/gl/ChartOfAccountsPage'));
const JournalEntriesPage = lazy(() => import('@/features/gl/JournalEntriesPage'));
const JournalEntryFormPage = lazy(() => import('@/features/gl/JournalEntryFormPage'));
const FinancialStatementsPage = lazy(() => import('@/features/finance/FinancialStatementsPage'));
const TaxCompliancePage = lazy(() => import('@/features/finance/TaxCompliancePage'));
const BudgetsForecastingPage = lazy(() => import('@/features/finance/BudgetsForecastingPage'));
const PipelineSettingsPage = lazy(() => import('@/features/crm/PipelineSettingsPage'));
const TerritoriesPage = lazy(() => import('@/features/crm/TerritoriesPage'));
const ServicesPage = lazy(() => import('@/features/crm/ServicesPage'));
const HrmsLayout = lazy(() => import('@/features/hrms/HrmsLayout'));
const HrmsDashboardPage = lazy(() => import('@/features/hrms/HrmsDashboardPage'));
const EmployeesListPage = lazy(() => import('@/features/hrms/EmployeesListPage'));
const EmployeeDetailPage = lazy(() => import('@/features/hrms/EmployeeDetailPage'));
const EmployeeFormPage = lazy(() => import('@/features/hrms/EmployeeFormPage'));
const DepartmentsPage = lazy(() => import('@/features/hrms/DepartmentsPage'));
const HolidaysPage = lazy(() => import('@/features/hrms/HolidaysPage'));
const LeavePage = lazy(() => import('@/features/hrms/LeavePage'));
const WfhPage = lazy(() => import('@/features/hrms/WfhPage'));
const AttendanceSheetPage = lazy(() => import('@/features/hrms/AttendanceSheetPage'));
const MyAttendancePage = lazy(() => import('@/features/hrms/MyAttendancePage'));
const AttendanceAdminPage = lazy(() => import('@/features/hrms/AttendanceAdminPage'));
const MonitoringAdminPage = lazy(() => import('@/features/hrms/MonitoringAdminPage'));
const AccountSecurityPage = lazy(() => import('@/features/account/AccountSecurityPage'));
const MyLeavePage = lazy(() => import('@/features/hrms/MyLeavePage'));
const MyRemoteWorkPage = lazy(() => import('@/features/hrms/MyRemoteWorkPage'));
const MyPayslipsPage = lazy(() => import('@/features/hrms/MyPayslipsPage'));
const PayrollPage = lazy(() => import('@/features/hrms/PayrollPage'));
const BonusesPage = lazy(() => import('@/features/hrms/BonusesPage'));
const LoansPage = lazy(() => import('@/features/hrms/LoansPage'));
const RecruitmentPage = lazy(() => import('@/features/hrms/RecruitmentPage'));
const PerformancePage = lazy(() => import('@/features/hrms/PerformancePage'));
const ExitManagementPage = lazy(() => import('@/features/hrms/ExitManagementPage'));
const DocumentsPage = lazy(() => import('@/features/documents/DocumentsPage'));
const WorkflowPage = lazy(() => import('@/features/workflow/WorkflowPage'));
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'));
const RolesPage = lazy(() => import('@/features/roles/RolesPage'));
const UsersPage = lazy(() => import('@/features/users/UsersPage'));
const BillingPage = lazy(() => import('@/features/billing/BillingPage'));
const AuditLogPage = lazy(() => import('@/features/audit/AuditLogPage'));
const PortalAccessPage = lazy(() => import('@/features/settings/PortalAccessPage'));
const CalendarIntegrationsPage = lazy(() => import('@/features/settings/CalendarIntegrationsPage'));
const LeadSourcesPage = lazy(() => import('@/features/settings/LeadSourcesPage'));
const LeadOptionsPage = lazy(() => import('@/features/settings/LeadOptionsPage'));
const LeadGeoOptionsPage = lazy(() => import('@/features/settings/LeadGeoOptionsPage'));
const LineItemDescriptionsPage = lazy(() => import('@/features/settings/LineItemDescriptionsPage'));
const WebhookEndpointsPage = lazy(() => import('@/features/settings/WebhookEndpointsPage'));
const MailboxConnectionsPage = lazy(() => import('@/features/settings/MailboxConnectionsPage'));
const WhatsAppConnectionsPage = lazy(() => import('@/features/settings/WhatsAppConnectionsPage'));
const ComplianceSettingsPage = lazy(() => import('@/features/compliance/ComplianceSettingsPage'));
const PortalShell = lazy(() => import('@/features/portal/PortalShell'));
const PortalOverviewPage = lazy(() => import('@/features/portal/PortalOverviewPage'));
const PortalProjectsPage = lazy(() => import('@/features/portal/PortalProjectsPage'));
const PortalProjectDetailPage = lazy(() => import('@/features/portal/PortalProjectDetailPage'));
const PortalInvoicesPage = lazy(() => import('@/features/portal/PortalInvoicesPage'));
const PortalInvoiceDetailPage = lazy(() => import('@/features/portal/PortalInvoiceDetailPage'));
const PortalPurchaseOrdersPage = lazy(() => import('@/features/portal/PortalPurchaseOrdersPage'));
const PortalPurchaseOrderDetailPage = lazy(() => import('@/features/portal/PortalPurchaseOrderDetailPage'));
const PortalVendorInvoicesPage = lazy(() => import('@/features/portal/PortalVendorInvoicesPage'));
const PortalDocumentsPage = lazy(() => import('@/features/portal/PortalDocumentsPage'));
const PortalTicketsPage = lazy(() => import('@/features/portal/PortalTicketsPage'));
const PortalTicketDetailPage = lazy(() => import('@/features/portal/PortalTicketDetailPage'));

export const router = createBrowserRouter([
  {
    element: <RequireGuest />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/otp-login', element: <OtpLoginPage /> },
    ],
  },
  // Standalone (not guest-guarded): it consumes the OAuth redirect and
  // establishes the session itself, so it must run even as auth state flips
  // mid-mount rather than be redirected away by the guest guard.
  { path: '/oauth/callback', element: <OAuthCallbackPage /> },
  // Standalone: needs to be publicly reachable so third-party OAuth apps
  // (e.g. the Meta App backing "Connect with Facebook") can link to it as
  // their Privacy Policy / Terms of Service URLs without a signed-in session.
  { path: '/privacy-policy', element: <PrivacyPolicyPage /> },
  { path: '/terms-of-service', element: <TermsOfServicePage /> },
  // Standalone (not guest-guarded, not RequireAuth-guarded): a sequence email's
  // unsubscribe link is opened by an anonymous recipient, not a signed-in user.
  { path: '/unsubscribe/:token', element: <UnsubscribePage /> },
  // Standalone: a quotation's "Review & sign" email link is opened by the
  // external client, not a signed-in user — same shape as /unsubscribe/:token.
  { path: '/sign/quote/:token', element: <QuoteSigningPage /> },
  {
    element: <RequireAuth />,
    children: [
      // Platform console: authenticated but OUTSIDE the tenant AppShell — it's
      // cross-tenant, with its own console chrome, and gated to platform admins.
      {
        element: <RequirePlatformAdmin />,
        children: [
          {
            path: '/platform',
            element: <PlatformShell />,
            children: [
              { index: true, element: <PlatformOverviewPage /> },
              { path: 'tenants', element: <PlatformTenantsPage /> },
              { path: 'plans', element: <PlatformPlansPage /> },
              { path: 'revenue', element: <PlatformRevenuePage /> },
              { path: 'usage', element: <PlatformUsagePage /> },
              { path: 'audit-log', element: <PlatformAuditLogPage /> },
              { path: 'tickets', element: <PlatformTicketsPage /> },
              { path: 'settings', element: <PlatformSettingsPage /> },
              { path: 'admins', element: <PlatformAdminsPage /> },
              { path: 'marketplace-apps', element: <PlatformMarketplaceReviewPage /> },
            ],
          },
        ],
      },
      // Client/Vendor portal: authenticated but OUTSIDE the tenant AppShell —
      // a different audience (an external company/vendor contact) seeing a
      // narrow, row-scoped slice of their own data, with its own chrome.
      {
        element: <RequirePortalRole />,
        children: [
          {
            path: '/portal',
            element: <PortalShell />,
            children: [
              { index: true, element: <PortalOverviewPage /> },
              { path: 'projects', element: <PortalProjectsPage /> },
              { path: 'projects/:id', element: <PortalProjectDetailPage /> },
              { path: 'invoices', element: <PortalInvoicesPage /> },
              { path: 'invoices/:id', element: <PortalInvoiceDetailPage /> },
              { path: 'purchase-orders', element: <PortalPurchaseOrdersPage /> },
              { path: 'purchase-orders/:id', element: <PortalPurchaseOrderDetailPage /> },
              { path: 'vendor-invoices', element: <PortalVendorInvoicesPage /> },
              { path: 'documents', element: <PortalDocumentsPage /> },
              { path: 'tickets', element: <PortalTicketsPage /> },
              { path: 'tickets/:id', element: <PortalTicketDetailPage /> },
            ],
          },
        ],
      },
      // QR attendance: both are standalone, chrome-free pages — the kiosk
      // display is meant for a shared screen (not the logged-in device's own
      // nav), and the scan-confirm page is a fast mobile landing page after
      // scanning, not a normal in-app view.
      {
        path: '/attendance/kiosk',
        element: <RequirePermission anyOf={['attendance:manage']} />,
        children: [{ index: true, element: <KioskDisplayPage /> }],
      },
      {
        path: '/attendance/scan',
        element: <RequirePermission anyOf={['attendance:create']} />,
        children: [{ index: true, element: <ScanConfirmPage /> }],
      },
      {
        element: <RequireStaffRole />,
        children: [{
        element: <AppShell />,
        children: [
          { index: true, element: <DashboardPage /> },
          {
            path: 'dashboard/executive',
            element: <RequirePermission anyOf={['report:read']} />,
            children: [{ index: true, element: <ExecutiveDashboardPage /> }],
          },
          {
            path: 'crm',
            // Any CRM read grants entry; each tab still narrows to its own resource.
            element: <RequirePermission anyOf={['lead:read', 'deal:read', 'company:read', 'contact:read']} />,
            children: [
              // Standalone lead pages (no CRM tab bar). Declared before the tabbed
              // layout so "new" isn't parsed as a lead id.
              {
                path: 'leads/new',
                element: <RequirePermission anyOf={['lead:create']} />,
                children: [{ index: true, element: <LeadFormPage /> }],
              },
              {
                path: 'leads/:id/edit',
                element: <RequirePermission anyOf={['lead:update']} />,
                children: [{ index: true, element: <LeadFormPage /> }],
              },
              {
                path: 'leads/:id',
                element: <RequirePermission anyOf={['lead:read']} />,
                children: [{ index: true, element: <LeadDetailPage /> }],
              },
              // Standalone deal pages, same shadowing rules as leads.
              {
                path: 'deals/new',
                element: <RequirePermission anyOf={['deal:create']} />,
                children: [{ index: true, element: <DealFormPage /> }],
              },
              {
                path: 'deals/:id/edit',
                element: <RequirePermission anyOf={['deal:update']} />,
                children: [{ index: true, element: <DealFormPage /> }],
              },
              {
                path: 'deals/:id',
                element: <RequirePermission anyOf={['deal:read']} />,
                children: [{ index: true, element: <DealDetailPage /> }],
              },
              {
                path: 'pipelines',
                element: <RequirePermission anyOf={['pipeline:read']} />,
                children: [{ index: true, element: <PipelineSettingsPage /> }],
              },
              {
                path: 'territories',
                element: <RequirePermission anyOf={['territory:read']} />,
                children: [{ index: true, element: <TerritoriesPage /> }],
              },
              {
                path: 'services',
                element: <RequirePermission anyOf={['service:read']} />,
                children: [{ index: true, element: <ServicesPage /> }],
              },
              {
                path: 'sequences',
                element: <RequirePermission anyOf={['sequence:read']} />,
                children: [
                  { index: true, element: <SequencesListPage /> },
                  { path: ':id', element: <SequenceDetailPage /> },
                ],
              },
              // Standalone company pages.
              {
                path: 'companies/new',
                element: <RequirePermission anyOf={['company:create']} />,
                children: [{ index: true, element: <CompanyFormPage /> }],
              },
              {
                path: 'companies/:id/edit',
                element: <RequirePermission anyOf={['company:update']} />,
                children: [{ index: true, element: <CompanyFormPage /> }],
              },
              {
                path: 'companies/:id',
                element: <RequirePermission anyOf={['company:read']} />,
                children: [{ index: true, element: <CompanyDetailPage /> }],
              },
              // Standalone contact pages.
              {
                path: 'contacts/new',
                element: <RequirePermission anyOf={['contact:create']} />,
                children: [{ index: true, element: <ContactFormPage /> }],
              },
              {
                path: 'contacts/:id/edit',
                element: <RequirePermission anyOf={['contact:update']} />,
                children: [{ index: true, element: <ContactFormPage /> }],
              },
              {
                path: 'contacts/:id',
                element: <RequirePermission anyOf={['contact:read']} />,
                children: [{ index: true, element: <ContactDetailPage /> }],
              },
              // Tabbed CRM pages share the layout (Leads / Deals / Companies / Contacts).
              {
                element: <CrmLayout />,
                children: [
                  // Index needs its own lead:read check now that the parent gate
                  // admits any CRM reader; others land on their first readable tab.
                  {
                    index: true,
                    element: (
                      <RequirePermission anyOf={['lead:read']} fallback={<CrmFallback />}>
                        <LeadsListPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'deals',
                    element: <RequirePermission anyOf={['deal:read']} />,
                    children: [{ index: true, element: <DealsBoardPage /> }],
                  },
                  {
                    path: 'companies',
                    element: <RequirePermission anyOf={['company:read']} />,
                    children: [{ index: true, element: <CompaniesListPage /> }],
                  },
                  {
                    path: 'contacts',
                    element: <RequirePermission anyOf={['contact:read']} />,
                    children: [{ index: true, element: <ContactsListPage /> }],
                  },
                ],
              },
            ],
          },
          {
            path: 'quotations',
            element: <RequirePermission anyOf={['quote:read']} />,
            children: [
              { index: true, element: <QuotationsListPage /> },
              { path: 'templates', element: <QuoteTemplatesPage /> },
            ],
          },
          {
            path: 'ai-employees',
            element: <RequirePermission anyOf={['aiEmployee:read']} />,
            children: [{ index: true, element: <AiEmployeesPage /> }],
          },
          {
            path: 'ai-assistant',
            element: <RequirePermission anyOf={['aiAssistant:read']} />,
            children: [{ index: true, element: <AiAssistantPage /> }],
          },
          {
            path: 'inbox',
            element: <RequirePermission anyOf={['mailbox:read']} />,
            children: [{ index: true, element: <InboxPage /> }],
          },
          {
            path: 'whatsapp',
            element: <RequirePermission anyOf={['whatsapp:read']} />,
            children: [{ index: true, element: <WhatsAppInboxPage /> }],
          },
          {
            path: 'portal-tickets',
            element: <RequirePermission anyOf={['portalTicket:read']} />,
            children: [{ index: true, element: <PortalTicketsInboxPage /> }],
          },
          {
            path: 'whatsapp/campaigns',
            element: <RequirePermission anyOf={['whatsapp:read']} />,
            children: [
              { index: true, element: <WhatsAppCampaignsListPage /> },
              { path: ':id', element: <WhatsAppCampaignDetailPage /> },
            ],
          },
          {
            path: 'agency/retainers',
            element: <RequirePermission anyOf={['retainer:read']} />,
            children: [{ index: true, element: <RetainersPage /> }],
          },
          {
            path: 'agency/campaigns',
            element: <RequirePermission anyOf={['marketingCampaign:read']} />,
            children: [{ index: true, element: <MarketingCampaignsPage /> }],
          },
          {
            path: 'developer-portal',
            element: <RequirePermission anyOf={['marketplaceApp:read']} />,
            children: [{ index: true, element: <MyAppsPage /> }],
          },
          {
            path: 'marketplace',
            element: <RequirePermission anyOf={['pluginInstallation:read']} />,
            children: [{ index: true, element: <MarketplaceBrowsePage /> }],
          },
          {
            path: 'marketplace/installed',
            element: <RequirePermission anyOf={['pluginInstallation:read']} />,
            children: [{ index: true, element: <MyInstallationsPage /> }],
          },
          {
            path: 'finance',
            children: [
              { index: true, element: <FinanceFallback /> },
              // Standalone finance pages (no Finance tab bar). Declared before the
              // tabbed layout so "invoices/new" etc. aren't shadowed.
              {
                path: 'invoices',
                element: <RequirePermission anyOf={['invoice:read']} />,
                children: [{ index: true, element: <InvoicesListPage /> }],
              },
              {
                path: 'invoices/new',
                element: <RequirePermission anyOf={['invoice:create']} />,
                children: [{ index: true, element: <InvoiceFormPage /> }],
              },
              {
                path: 'invoices/:id/edit',
                element: <RequirePermission anyOf={['invoice:update']} />,
                children: [{ index: true, element: <InvoiceFormPage /> }],
              },
              {
                path: 'invoices/:id',
                element: <RequirePermission anyOf={['invoice:read']} />,
                children: [{ index: true, element: <InvoiceDetailPage /> }],
              },
              {
                path: 'templates',
                element: <RequirePermission anyOf={['invoice:update']} />,
                children: [{ index: true, element: <InvoiceTemplatesPage /> }],
              },
              {
                path: 'settings',
                element: <RequirePermission anyOf={['setting:read']} />,
                children: [{ index: true, element: <FinanceSettingsPage /> }],
              },
              {
                path: 'recurring',
                element: <RequirePermission anyOf={['invoice:read']} />,
                children: [{ index: true, element: <RecurringInvoicesPage /> }],
              },
              {
                path: 'banks',
                element: <RequirePermission anyOf={['bank:read']} />,
                children: [{ index: true, element: <BankAccountsPage /> }],
              },
              {
                path: 'payments',
                element: <RequirePermission anyOf={['payment:read']} />,
                children: [{ index: true, element: <PaymentsPage /> }],
              },
              {
                path: 'customer-ledger',
                element: <RequirePermission anyOf={['invoice:read']} />,
                children: [{ index: true, element: <CustomerLedgerPage /> }],
              },
              {
                path: 'advances',
                element: <RequirePermission anyOf={['payment:read']} />,
                children: [{ index: true, element: <AdvancesPage /> }],
              },
              {
                path: 'expenses',
                element: <RequirePermission anyOf={['expense:read']} />,
                children: [{ index: true, element: <ExpensesListPage /> }],
              },
              {
                path: 'expenses/categories',
                element: <RequirePermission anyOf={['expense:read']} />,
                children: [{ index: true, element: <ExpenseCategoriesPage /> }],
              },
              {
                path: 'entries/new',
                element: <RequirePermission anyOf={['journalEntry:create']} />,
                children: [{ index: true, element: <JournalEntryFormPage /> }],
              },
              // Tabbed Finance pages share the layout (Overview / Chart of Accounts /
              // General Ledger / Financial Statements / Tax & Compliance / Budgets & Forecasting).
              {
                element: <FinanceLayout />,
                children: [
                  {
                    path: 'overview',
                    element: <RequirePermission anyOf={['invoice:read']} />,
                    children: [{ index: true, element: <FinanceDashboardPage /> }],
                  },
                  {
                    path: 'accounts',
                    element: <RequirePermission anyOf={['ledgerAccount:read']} />,
                    children: [{ index: true, element: <ChartOfAccountsPage /> }],
                  },
                  {
                    path: 'ledger',
                    element: <RequirePermission anyOf={['journalEntry:read']} />,
                    children: [{ index: true, element: <JournalEntriesPage /> }],
                  },
                  {
                    path: 'statements',
                    element: <RequirePermission anyOf={['journalEntry:read']} />,
                    children: [{ index: true, element: <FinancialStatementsPage /> }],
                  },
                  {
                    path: 'tax',
                    element: <RequirePermission anyOf={['taxFiling:read']} />,
                    children: [{ index: true, element: <TaxCompliancePage /> }],
                  },
                  {
                    path: 'budgets',
                    element: <RequirePermission anyOf={['budgetLine:read']} />,
                    children: [{ index: true, element: <BudgetsForecastingPage /> }],
                  },
                ],
              },
            ],
          },
          {
            path: 'reports',
            element: <RequirePermission anyOf={['report:read']} />,
            children: [
              { index: true, element: <ReportsPage /> },
              { path: 'builder', element: <ReportBuilderPage /> },
              { path: 'projects-tasks', element: <ProjectTaskReportsPage /> },
              { path: 'sales', element: <SalesReportsPage /> },
              {
                path: 'sales/targets',
                element: <RequirePermission anyOf={['salesTarget:read']} />,
                children: [{ index: true, element: <SalesTargetsPage /> }],
              },
            ],
          },
          // No RequirePermission wrapper — self-serve for every authenticated
          // user, matching the backend (GET /assets/mine has no authorize()
          // gate; it can only ever return the caller's own equipment).
          { path: 'assets/mine', element: <MyAssetsPage /> },
          // Same pattern for the Employee self-service sidebar — each backend
          // route (GET .../mine) can only ever return the caller's own data.
          { path: 'attendance/mine', element: <MyAttendancePage /> },
          // Personal security settings (MFA) — every signed-in user manages
          // their own, with no permission gate, same self-service pattern.
          { path: 'account/security', element: <AccountSecurityPage /> },
          { path: 'leave/mine', element: <MyLeavePage /> },
          { path: 'remote-work/mine', element: <MyRemoteWorkPage /> },
          { path: 'payslips/mine', element: <MyPayslipsPage /> },
          {
            path: 'holidays',
            element: <RequirePermission anyOf={['holiday:read']} />,
            children: [{ index: true, element: <HolidaysPage /> }],
          },
          {
            path: 'assets',
            element: <RequirePermission anyOf={['asset:read']} />,
            children: [
              { index: true, element: <AssetsListPage /> },
              // Declared before ":id" so "new"/"categories" aren't parsed as an asset id.
              {
                path: 'new',
                element: <RequirePermission anyOf={['asset:create']} />,
                children: [{ index: true, element: <AssetFormPage /> }],
              },
              {
                path: 'categories',
                element: <RequirePermission anyOf={['asset:create', 'asset:update']} />,
                children: [{ index: true, element: <AssetCategoriesPage /> }],
              },
              {
                path: ':id/edit',
                element: <RequirePermission anyOf={['asset:update']} />,
                children: [{ index: true, element: <AssetFormPage /> }],
              },
              { path: ':id', element: <AssetDetailPage /> },
            ],
          },
          {
            path: 'inventory',
            element: <RequirePermission anyOf={['product:read']} />,
            children: [
              { index: true, element: <InventoryListPage /> },
              // Declared before ":id" so "new"/"categories"/"warehouses" aren't parsed as a product id.
              {
                path: 'new',
                element: <RequirePermission anyOf={['product:create']} />,
                children: [{ index: true, element: <ProductFormPage /> }],
              },
              {
                path: 'categories',
                element: <RequirePermission anyOf={['product:create', 'product:update']} />,
                children: [{ index: true, element: <ProductCategoriesPage /> }],
              },
              {
                path: 'warehouses',
                element: <RequirePermission anyOf={['product:manage']} />,
                children: [{ index: true, element: <WarehousesPage /> }],
              },
              {
                path: ':id/edit',
                element: <RequirePermission anyOf={['product:update']} />,
                children: [{ index: true, element: <ProductFormPage /> }],
              },
              { path: ':id', element: <ProductDetailPage /> },
            ],
          },
          {
            path: 'procurement',
            children: [
              { index: true, element: <ProcurementFallback /> },
              // Standalone vendor pages (no procurement tab bar). Declared before
              // the tabbed layout so "new"/"categories"/":id" aren't shadowed.
              {
                path: 'vendors/new',
                element: <RequirePermission anyOf={['vendor:create']} />,
                children: [{ index: true, element: <VendorFormPage /> }],
              },
              {
                path: 'vendors/categories',
                element: <RequirePermission anyOf={['vendor:create', 'vendor:update']} />,
                children: [{ index: true, element: <VendorCategoriesPage /> }],
              },
              {
                path: 'vendors/:id/edit',
                element: <RequirePermission anyOf={['vendor:update']} />,
                children: [{ index: true, element: <VendorFormPage /> }],
              },
              { path: 'vendors/:id', element: <VendorDetailPage /> },
              // Standalone purchase-request pages.
              {
                path: 'purchase-requests/new',
                element: <RequirePermission anyOf={['purchaseRequest:create']} />,
                children: [{ index: true, element: <PurchaseRequestFormPage /> }],
              },
              {
                path: 'purchase-requests/:id/edit',
                element: <RequirePermission anyOf={['purchaseRequest:create']} />,
                children: [{ index: true, element: <PurchaseRequestFormPage /> }],
              },
              { path: 'purchase-requests/:id', element: <PurchaseRequestDetailPage /> },
              // Standalone RFQ pages.
              {
                path: 'rfqs/new',
                element: <RequirePermission anyOf={['rfq:create']} />,
                children: [{ index: true, element: <RfqFormPage /> }],
              },
              {
                path: 'rfqs/:id/edit',
                element: <RequirePermission anyOf={['rfq:update']} />,
                children: [{ index: true, element: <RfqFormPage /> }],
              },
              { path: 'rfqs/:id', element: <RfqDetailPage /> },
              // Standalone purchase-order pages.
              {
                path: 'purchase-orders/new',
                element: <RequirePermission anyOf={['purchaseOrder:create']} />,
                children: [{ index: true, element: <PurchaseOrderFormPage /> }],
              },
              {
                path: 'purchase-orders/:id/edit',
                element: <RequirePermission anyOf={['purchaseOrder:update']} />,
                children: [{ index: true, element: <PurchaseOrderFormPage /> }],
              },
              { path: 'purchase-orders/:id', element: <PurchaseOrderDetailPage /> },
              // Tabbed procurement pages share the layout (Purchase Requests / Vendors / RFQs / Purchase Orders).
              {
                element: <ProcurementLayout />,
                children: [
                  {
                    path: 'purchase-requests',
                    element: <RequirePermission anyOf={['purchaseRequest:read']} />,
                    children: [{ index: true, element: <PurchaseRequestsListPage /> }],
                  },
                  {
                    path: 'vendors',
                    element: <RequirePermission anyOf={['vendor:read']} />,
                    children: [{ index: true, element: <VendorsListPage /> }],
                  },
                  {
                    path: 'rfqs',
                    element: <RequirePermission anyOf={['rfq:read']} />,
                    children: [{ index: true, element: <RfqsListPage /> }],
                  },
                  {
                    path: 'purchase-orders',
                    element: <RequirePermission anyOf={['purchaseOrder:read']} />,
                    children: [{ index: true, element: <PurchaseOrdersListPage /> }],
                  },
                ],
              },
            ],
          },
          {
            // /gl merged into /finance as tabs — these are thin redirects for old
            // bookmarks/links, not a real section anymore.
            path: 'gl',
            children: [
              { index: true, element: <Navigate to="/finance" replace /> },
              { path: 'accounts', element: <Navigate to="/finance/accounts" replace /> },
              { path: 'entries', element: <Navigate to="/finance/ledger" replace /> },
              { path: 'entries/new', element: <Navigate to="/finance/entries/new" replace /> },
              { path: 'reports', element: <Navigate to="/finance/statements?tab=trial-balance" replace /> },
              { path: 'reports/trial-balance', element: <Navigate to="/finance/statements?tab=trial-balance" replace /> },
              { path: 'reports/pnl', element: <Navigate to="/finance/statements?tab=pnl" replace /> },
              { path: 'reports/balance-sheet', element: <Navigate to="/finance/statements?tab=balance-sheet" replace /> },
              { path: 'reports/cash-flow', element: <Navigate to="/finance/statements?tab=cash-flow" replace /> },
            ],
          },
          {
            path: 'projects',
            element: <RequirePermission anyOf={['project:read']} />,
            children: [
              { index: true, element: <ProjectsListPage /> },
              // Declared before ":id" so "new"/"settings"/"portfolio" aren't parsed as a project id.
              { path: 'portfolio', element: <ProjectPortfolioPage /> },
              {
                path: 'new',
                element: <RequirePermission anyOf={['project:create']} />,
                children: [{ index: true, element: <CreateProjectPage /> }],
              },
              {
                path: 'settings',
                element: <RequirePermission anyOf={['projectCategory:read', 'projectTemplate:read']} />,
                children: [{ index: true, element: <ProjectCategoriesTemplatesPage /> }],
              },
              // Declared before ":id" so "edit"/"settings" are never parsed as a project id.
              {
                path: ':id/edit',
                element: <RequirePermission anyOf={['project:update']} />,
                children: [{ index: true, element: <EditProjectPage /> }],
              },
              {
                path: ':id/settings',
                element: <RequirePermission anyOf={['project:update']} />,
                children: [{ index: true, element: <ProjectSettingsPage /> }],
              },
              {
                path: ':id',
                element: <ProjectLayout />,
                // Tabs are nested routes, so the view is in the URL and the
                // project is fetched once by the layout, not per tab.
                children: [
                  { index: true, element: <OverviewTab /> },
                  { path: 'board', element: <BoardTab /> },
                  { path: 'sprints', element: <SprintsTab /> },
                  {
                    path: 'milestones',
                    element: <RequirePermission anyOf={['milestone:read']} />,
                    children: [{ index: true, element: <MilestonesTab /> }],
                  },
                  { path: 'timeline', element: <TimelineTab /> },
                  {
                    path: 'discussion',
                    element: <RequirePermission anyOf={['discussion:read']} />,
                    children: [{ index: true, element: <DiscussionTab /> }],
                  },
                  {
                    path: 'chat',
                    element: <RequirePermission anyOf={['chat:read']} />,
                    children: [{ index: true, element: <ChatTab /> }],
                  },
                ],
              },
            ],
          },
          {
            path: 'tasks',
            element: <RequirePermission anyOf={['task:read']} />,
            children: [
              { index: true, element: <TasksListPage /> },
              // Before ":id" so "new" isn't parsed as a task id.
              {
                path: 'new',
                element: <RequirePermission anyOf={['task:create']} />,
                children: [{ index: true, element: <CreateTaskPage /> }],
              },
              // Before ":id" so "edit" isn't parsed as a task id.
              {
                path: ':id/edit',
                element: <RequirePermission anyOf={['task:update']} />,
                children: [{ index: true, element: <EditTaskPage /> }],
              },
              { path: ':id', element: <TaskDetailPage /> },
            ],
          },
          {
            path: 'calendar',
            element: <RequirePermission anyOf={['event:read']} />,
            children: [{ index: true, element: <CalendarPage /> }],
          },
          // Notifications are user-owned — being signed in is the only gate.
          { path: 'notifications', element: <NotificationsPage /> },
          {
            path: 'hrms',
            element: <RequirePermission anyOf={['employee:read']} />,
            children: [
              // Standalone employee pages (no HRMS tab bar). Declared before the
              // tabbed layout so "new"/"edit" aren't shadowed by the list route.
              {
                path: 'employees/new',
                element: <RequirePermission anyOf={['employee:create']} />,
                children: [{ index: true, element: <EmployeeFormPage /> }],
              },
              {
                path: 'employees/:id/edit',
                element: <RequirePermission anyOf={['employee:update']} />,
                children: [{ index: true, element: <EmployeeFormPage /> }],
              },
              { path: 'employees/:id', element: <EmployeeDetailPage /> },
              // Tabbed HRMS pages share the layout (Overview / Employees / …).
              {
                element: <HrmsLayout />,
                children: [
                  { index: true, element: <HrmsDashboardPage /> },
                  { path: 'employees', element: <EmployeesListPage /> },
                  { path: 'departments', element: <DepartmentsPage /> },
                  {
                    path: 'holidays',
                    element: <RequirePermission anyOf={['holiday:read']} />,
                    children: [{ index: true, element: <HolidaysPage /> }],
                  },
                  {
                    path: 'attendance',
                    element: <RequirePermission anyOf={['attendance:read']} />,
                    children: [{ index: true, element: <AttendanceSheetPage /> }],
                  },
                  {
                    path: 'attendance-setup',
                    element: <RequirePermission anyOf={['attendanceLocation:manage']} />,
                    children: [{ index: true, element: <AttendanceAdminPage /> }],
                  },
                  {
                    path: 'monitoring',
                    element: <RequirePermission anyOf={['monitoring:manage']} />,
                    children: [{ index: true, element: <MonitoringAdminPage /> }],
                  },
                  {
                    path: 'leave',
                    element: <RequirePermission anyOf={['leave:read']} />,
                    children: [{ index: true, element: <LeavePage /> }],
                  },
                  {
                    path: 'wfh',
                    element: <RequirePermission anyOf={['wfh:read']} />,
                    children: [{ index: true, element: <WfhPage /> }],
                  },
                  {
                    path: 'payroll',
                    element: <RequirePermission anyOf={['payroll:read']} />,
                    children: [{ index: true, element: <PayrollPage /> }],
                  },
                  {
                    path: 'bonuses',
                    element: <RequirePermission anyOf={['payroll:read']} />,
                    children: [{ index: true, element: <BonusesPage /> }],
                  },
                  {
                    path: 'loans',
                    element: <RequirePermission anyOf={['payroll:read']} />,
                    children: [{ index: true, element: <LoansPage /> }],
                  },
                  {
                    path: 'recruitment',
                    element: <RequirePermission anyOf={['recruitment:read']} />,
                    children: [{ index: true, element: <RecruitmentPage /> }],
                  },
                  {
                    path: 'performance',
                    element: <RequirePermission anyOf={['performance:read']} />,
                    children: [{ index: true, element: <PerformancePage /> }],
                  },
                  {
                    path: 'exit',
                    element: <RequirePermission anyOf={['exit:read']} />,
                    children: [{ index: true, element: <ExitManagementPage /> }],
                  },
                ],
              },
            ],
          },
          {
            path: 'documents',
            element: <RequirePermission anyOf={['file:read']} />,
            children: [{ index: true, element: <DocumentsPage /> }],
          },
          {
            path: 'workflow',
            element: <RequirePermission anyOf={['automation:read']} />,
            children: [{ index: true, element: <WorkflowPage /> }],
          },
          {
            path: 'settings',
            element: <RequirePermission anyOf={['setting:read']} />,
            children: [{ index: true, element: <SettingsPage /> }],
          },
          {
            path: 'settings/portal-access',
            element: <RequirePermission anyOf={['user:read']} />,
            children: [{ index: true, element: <PortalAccessPage /> }],
          },
          {
            path: 'settings/calendar-integrations',
            element: <RequirePermission anyOf={['event:read']} />,
            children: [{ index: true, element: <CalendarIntegrationsPage /> }],
          },
          {
            path: 'settings/lead-sources',
            element: <RequirePermission anyOf={['lead:read']} />,
            children: [{ index: true, element: <LeadSourcesPage /> }],
          },
          {
            path: 'settings/lead-options',
            element: <RequirePermission anyOf={['lead:read']} />,
            children: [{ index: true, element: <LeadOptionsPage /> }],
          },
          {
            path: 'settings/lead-geo-options',
            element: <RequirePermission anyOf={['lead:read']} />,
            children: [{ index: true, element: <LeadGeoOptionsPage /> }],
          },
          {
            path: 'settings/line-item-descriptions',
            element: <RequirePermission anyOf={['setting:read']} />,
            children: [{ index: true, element: <LineItemDescriptionsPage /> }],
          },
          {
            path: 'settings/webhooks',
            element: <RequirePermission anyOf={['webhook:read']} />,
            children: [{ index: true, element: <WebhookEndpointsPage /> }],
          },
          {
            path: 'settings/mailboxes',
            element: <RequirePermission anyOf={['mailbox:manage']} />,
            children: [{ index: true, element: <MailboxConnectionsPage /> }],
          },
          {
            path: 'settings/whatsapp-numbers',
            element: <RequirePermission anyOf={['whatsapp:manage']} />,
            children: [{ index: true, element: <WhatsAppConnectionsPage /> }],
          },
          // No RequirePermission wrapper — self-serve for every authenticated
          // user, matching the backend (consents/export have no authorize() gate).
          { path: 'settings/compliance', element: <ComplianceSettingsPage /> },
          {
            path: 'users',
            element: <RequirePermission anyOf={['user:read']} />,
            children: [{ index: true, element: <UsersPage /> }],
          },
          {
            path: 'roles',
            element: <RequirePermission anyOf={['role:read']} />,
            children: [{ index: true, element: <RolesPage /> }],
          },
          {
            path: 'billing',
            element: <RequirePermission anyOf={['billing:read']} />,
            children: [{ index: true, element: <BillingPage /> }],
          },
          {
            path: 'audit',
            element: <RequirePermission anyOf={['audit:read']} />,
            children: [{ index: true, element: <AuditLogPage /> }],
          },
          { path: '403', element: <Forbidden /> },
          { path: '*', element: <NotFound /> },
        ],
      }],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);

/** /crm index for users without lead:read: land on their first readable tab. */
function CrmFallback() {
  const granted = toPermissionSet(useAppSelector(selectPermissions));
  const tab = canAny(granted, ['deal:read'])
    ? 'deals'
    : canAny(granted, ['company:read'])
      ? 'companies'
      : 'contacts';
  return <Navigate to={`/crm/${tab}`} replace />;
}

/** /procurement index: land on the first tab the user can actually read. */
function ProcurementFallback() {
  const granted = toPermissionSet(useAppSelector(selectPermissions));
  const tab = canAny(granted, ['purchaseRequest:read'])
    ? 'purchase-requests'
    : canAny(granted, ['vendor:read'])
      ? 'vendors'
      : canAny(granted, ['rfq:read'])
        ? 'rfqs'
        : 'purchase-orders';
  return <Navigate to={`/procurement/${tab}`} replace />;
}

/** /finance index: land on the first tab the user can actually read. */
function FinanceFallback() {
  const granted = toPermissionSet(useAppSelector(selectPermissions));
  const tab = canAny(granted, ['invoice:read'])
    ? 'overview'
    : canAny(granted, ['ledgerAccount:read'])
      ? 'accounts'
      : canAny(granted, ['journalEntry:read'])
        ? 'ledger'
        : canAny(granted, ['taxFiling:read'])
          ? 'tax'
          : 'budgets';
  return <Navigate to={`/finance/${tab}`} replace />;
}

function Forbidden() {
  return (
    <EmptyState
      title="You don’t have access"
      message="Your role doesn’t include permission for this area. Ask an administrator if you need it."
    />
  );
}

function NotFound() {
  return <EmptyState title="Page not found" message="The page you’re looking for doesn’t exist." />;
}
